import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ConversationReference } from '~/types/api.graphql';

import { AudiencePopoverPanel } from './AIInsightsSidebar.styles';
import AudiencePickerContent from './AudiencePickerContent';

interface AddAudiencePopoverProps {
  anchorRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  onClose: () => void;
  onSelectMention: (ref: ConversationReference) => void;
}

const AddAudiencePopover = ({ anchorRef, isOpen, onClose, onSelectMention }: AddAudiencePopoverProps) => {
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPanelStyle({ top: rect.top, left: rect.left, width: rect.width });
  }, [anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, updatePosition]);

  // Close when clicking/tapping outside both the panel and the anchor
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = (e instanceof TouchEvent ? e.touches[0]?.target : e.target) as Node | null;
      if (!target) return;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !panelStyle) return null;

  return ReactDOM.createPortal(
    <AudiencePopoverPanel
      ref={panelRef}
      $isOpen={isOpen}
      style={{
        top: panelStyle.top,
        left: panelStyle.left,
        width: panelStyle.width,
        transform: 'translateY(-100%)',
        marginTop: '-0.25rem',
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <AudiencePickerContent onSelectMention={onSelectMention} onClose={onClose} />
    </AudiencePopoverPanel>,
    document.body,
  );
};

export default AddAudiencePopover;
